import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { TIERS, REGISTER_URL, getCurrentTierIndex, formatBaht, getNextHint } from '../../pricing';

const { FiTag, FiCheck, FiClock } = FiIcons;

const Pricing = () => {
  const current = getCurrentTierIndex();
  const tier = current >= 0 ? TIERS[current] : null;

  const includedItems = [
    'ค่าเรือสปีดโบ๊ทเหมาเช่าลำ (ไป-กลับ)',
    'อาหารกลางวัน 1 มื้อ',
    'ค่าธรรมเนียมนักพรตผู้ทำพิธี',
    'ฮู้ (ยันต์จีนเสริมดวงเฉพาะตัว) 1 ชิ้น ติดตัวกลับบ้านทุกท่าน'
  ];

  const notIncludedItems = [
    'ไม่รวมค่าเครื่องสักการะและอุปกรณ์ในพิธี (ธูป เทียน)',
    'ผู้ร่วมพิธีต้องจัดหาเครื่องสักการะตามกำลังทรัพย์ของตน',
    'ห้ามซื้อเครื่องสักการะให้ผู้อื่น ต้องซื้อด้วยตนเอง (ซื้อได้ที่ด้านหน้าก่อนทำพิธี)'
  ];

  return (
    <section id="pricing" className="py-10 md:py-16 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            ค่าใช้จ่ายและรายละเอียด
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            เปิดลงทะเบียนแล้ว รับสิทธิ์ราคาพิเศษสำหรับ Early Bird
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-xl p-8"
            >
              <div className="text-center mb-8">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <SafeIcon icon={FiTag} className="w-8 h-8 text-red-600" />
                  <h3 className="text-2xl font-bold text-gray-900">
                    อัตราค่าลงทะเบียน
                  </h3>
                </div>

                {tier ? (
                  <>
                    <div className="relative">
                      <span className="text-5xl font-bold text-red-600">{formatBaht(tier.price)}</span>
                      <span className="text-xl text-gray-600 ml-2">บาท/ท่าน</span>
                    </div>
                    <p className="text-lg text-red-600 font-semibold mt-3">
                      {tier.badge}
                    </p>
                  </>
                ) : (
                  <p className="text-2xl font-bold text-gray-700">ปิดรับลงทะเบียนแล้ว</p>
                )}
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl p-5 text-center mb-6"
              >
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <SafeIcon icon={FiClock} className="w-5 h-5 text-white" />
                  <span className="font-bold text-white">
                    ราคาขยับขึ้นตามรอบ · ทริป 19 พ.ย. 69
                  </span>
                </div>
                <div className="space-y-1">
                  {TIERS.map((t, i) => (
                    <p
                      key={t.key}
                      className={`text-white ${i < current || current < 0 ? 'line-through opacity-70' : ''} ${i === current ? 'font-bold' : ''}`}
                    >
                      {t.line}
                    </p>
                  ))}
                </div>
              </motion.div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center mb-6">
                <p className="font-bold text-green-700">
                  {tier ? 'เปิดลงทะเบียนแล้ว · จำกัด 36 ท่าน' : 'ปิดรับลงทะเบียนแล้ว'}
                </p>
                <p className="text-green-700 mt-1">
                  {getNextHint()}
                </p>
              </div>

              <div className="text-center">
                <a
                  href={REGISTER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white text-lg font-semibold rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  ลงทะเบียนเข้าร่วมทริป
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  ราคานี้รวม
                </h3>
                <div className="space-y-3">
                  {includedItems.map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-gray-700">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  ไม่รวมในราคา
                </h3>
                <div className="space-y-2">
                  {notIncludedItems.map((item, index) => (
                    <p
                      key={index}
                      className="text-gray-700 pl-4 border-l-2 border-red-300"
                    >
                      {item}
                    </p>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;